import { useState, useCallback, useRef } from 'react';
import APIs from '../../../../../application/APIs';
import { Placeholder } from './types';

interface PlaceholdersState {
  placeholders: Placeholder[];
  selectedPlaceholderId: string | null;
  saving: boolean;
  // Legacy drawing state
  isDrawing: boolean;
  drawStart: { x: number; y: number } | null;
  drawCurrent: { x: number; y: number } | null;
}

interface UsePlaceholdersProps {
  templateId: string;
}

export const usePlaceholders = ({ templateId }: UsePlaceholdersProps) => {
  const [state, setState] = useState<PlaceholdersState>({
    placeholders: [],
    selectedPlaceholderId: null,
    saving: false,
    isDrawing: false,
    drawStart: null,
    drawCurrent: null,
  });

  // Use ref to always have access to latest placeholders (fixes stale closure in savePlaceholders)
  const placeholdersRef = useRef<Placeholder[]>([]);
  placeholdersRef.current = state.placeholders;

  // Ref for accessing current state in async callbacks
  const stateRef = useRef(state);
  stateRef.current = state;

  const setPlaceholders = useCallback((placeholders: Placeholder[]) => {
    setState(s => ({ ...s, placeholders }));
  }, []);

  const addPlaceholder = useCallback((placeholder: Placeholder) => {
    setState(s => ({
      ...s,
      placeholders: [...s.placeholders, placeholder],
    }));
  }, []);

  const addPlaceholderAndSelect = useCallback((placeholder: Placeholder) => {
    setState(s => ({
      ...s,
      placeholders: [...s.placeholders, placeholder],
      selectedPlaceholderId: placeholder.id,
    }));
  }, []);

  const updatePlaceholder = useCallback((id: string, updates: Partial<Placeholder>) => {
    setState(s => ({
      ...s,
      placeholders: s.placeholders.map(p =>
        p.id === id ? { ...p, ...updates } : p
      ),
    }));
  }, []);

  const deletePlaceholder = useCallback((id: string) => {
    setState(s => ({
      ...s,
      placeholders: s.placeholders.filter(p => p.id !== id),
      selectedPlaceholderId: s.selectedPlaceholderId === id ? null : s.selectedPlaceholderId,
    }));
  }, []);

  const selectPlaceholder = useCallback((id: string | null) => {
    setState(s => ({ ...s, selectedPlaceholderId: id }));
  }, []);

  const clearSelection = useCallback(() => {
    setState(s => ({ ...s, selectedPlaceholderId: null }));
  }, []);

  const savePlaceholders = useCallback(async () => {
    setState(s => ({ ...s, saving: true }));
    try {
      // Use ref to get latest placeholders (avoids stale closure)
      await APIs.saveTemplatePlaceholders(templateId, placeholdersRef.current);
      setState(s => ({ ...s, saving: false }));
      return true;
    } catch (error) {
      console.error('Failed to save placeholders:', error);
      setState(s => ({ ...s, saving: false }));
      return false;
    }
  }, [templateId]);

  const updatePlaceholdersValues = useCallback((updater: (placeholders: Placeholder[]) => Placeholder[]) => {
    setState(s => ({
      ...s,
      placeholders: updater(s.placeholders),
    }));
  }, []);

  // Legacy drawing functions (kept for compatibility, but single-click is now primary)
  const startDrawing = useCallback((x: number, y: number) => {
    setState(s => ({
      ...s,
      isDrawing: true,
      drawStart: { x, y },
      drawCurrent: { x, y },
      selectedPlaceholderId: null,
    }));
  }, []);

  const updateDrawing = useCallback((x: number, y: number) => {
    setState(s => {
      if (!s.isDrawing) return s;
      return { ...s, drawCurrent: { x, y } };
    });
  }, []);

  const cancelDrawing = useCallback(() => {
    setState(s => ({
      ...s,
      isDrawing: false,
      drawStart: null,
      drawCurrent: null,
    }));
  }, []);

  const getCurrentPagePlaceholders = useCallback((currentPage: number) => {
    return state.placeholders.filter(p => p.page === currentPage);
  }, [state.placeholders]);

  const getSelectedPlaceholder = useCallback(() => {
    return state.placeholders.find(p => p.id === state.selectedPlaceholderId) || null;
  }, [state.placeholders, state.selectedPlaceholderId]);

  const getDrawingRect = useCallback(() => {
    if (!state.isDrawing || !state.drawStart || !state.drawCurrent) {
      return null;
    }
    const minX = Math.min(state.drawStart.x, state.drawCurrent.x);
    const minY = Math.min(state.drawStart.y, state.drawCurrent.y);
    const w = Math.abs(state.drawCurrent.x - state.drawStart.x);
    const h = Math.abs(state.drawCurrent.y - state.drawStart.y);
    return { x: minX, y: minY, w, h };
  }, [state.isDrawing, state.drawStart, state.drawCurrent]);

  return {
    placeholders: state.placeholders,
    selectedPlaceholderId: state.selectedPlaceholderId,
    saving: state.saving,
    isDrawing: state.isDrawing,
    drawStart: state.drawStart,
    drawCurrent: state.drawCurrent,
    placeholdersRef,
    stateRef,
    setPlaceholders,
    addPlaceholder,
    addPlaceholderAndSelect,
    updatePlaceholder,
    deletePlaceholder,
    selectPlaceholder,
    clearSelection,
    savePlaceholders,
    updatePlaceholdersValues,
    startDrawing,
    updateDrawing,
    cancelDrawing,
    getCurrentPagePlaceholders,
    getSelectedPlaceholder,
    getDrawingRect,
  };
};
